import React, { Component } from 'react';
import { ApolloProvider } from 'react-apollo';

import Loading from 'components/Loading';

import { bffCSRFLink, sendToAccessDenied, sendToLogin } from 'utils/bff';

import { CSRF_TOKEN } from '../../constants';

import apolloClient from './apolloClient';

class ApolloConnector extends Component {
  state = {
    csrfToken: undefined,
  };

  /**
   * Requests the CSRF token on mounting. The token is required
   * before making _any_ requests via the GraphQL endpoint.
   *
   * @memberof ApolloConnector
   */
  componentDidMount() {
    this.getCSRFToken();
  }

  /**
   * Clears the CSRF token on unmount. The CSRF token is session
   * based and may not be valid next time the user visits this
   * application.
   *
   * @memberof ApolloConnector
   */
  componentWillUnmount() {
    this.clearCSRFToken();
  }

  /**
   * Performs an async request for the CSRF token.
   * Sends user to login flow if unauthorized.
   * Persists token if successful.
   *
   * This `fetch` sends two requests: OPTIONS and GET. The OPTIONS request
   * _must NOT_ validate user auth or you will get an opaque response
   * (useless in JS). If the user is unauthorized it must be the GET
   * request only that returns the 401 (unauthorized) status.
   *
   * @memberof ApolloConnector
   */
  getCSRFToken = () => {
    fetch(bffCSRFLink, {
      credentials: 'include',
    })
      .then(response => {
        if (!response.ok) {
          throw response;
        }
        return response.text();
      })
      .then(csrfToken => {
        this.saveCSRFToken(csrfToken);
      })
      .catch(error => {
        if (error.status === 403) {
          sendToAccessDenied();
        }
        if (error.status === 401) {
          sendToLogin();
        }
      });
  };

  /**
   * Removes the CSRF token from the memoryDB
   *
   * @memberof ApolloConnector
   */
  clearCSRFToken = () => {
    memoryDB.removeItem(CSRF_TOKEN);
  };

  /**
   * Persists the CSRF token to the memoryDB and to state.
   * The state update triggers a re-render.
   *
   * @memberof ApolloConnector
   */
  saveCSRFToken = csrfToken => {
    memoryDB.setItem(CSRF_TOKEN, csrfToken);
    this.setState({ csrfToken });
  };

  /**
   * Evaluates if the CSRF token has been received and returns
   * either the Loading or the ApolloProvider component.
   *
   * Not returning the ApolloProvider until a CSRF token is
   * persisted prevents the app from making requests that are
   * guaranteed to fail.
   *
   * @returns React Component
   * @memberof ApolloConnector
   */
  render() {
    if (!this.state.csrfToken && process.env.NODE_ENV !== 'test') {
      return <Loading />;
    }

    return <ApolloProvider client={apolloClient} {...this.props} />;
  }
}

export default ApolloConnector;



// WEBPACK FOOTER //
// ./src/setup/ApolloConnector/index.js